<template>
  <template v-if="!isEditExternalVersion">
    <div class="c-form__fields c-project__version-component u-mts">
      <div class="c-project__versions-content">
        <div class="c-version__title-container">
          <h3 v-if="externalVersion.version_number || dateFormated" class="c-version__title u-mb0">{{ (externalVersion.name ? externalVersion.name + " - " : "") + externalVersion.version_number + " " + dateFormated }}</h3>
          <h3 v-else class="c-version__title u-mb0">{{ externalVersion.name }}</h3>
        </div>
        <div class="c-version__external-content-container">
          <p v-if="externalVersion.description" class="c-version__description u-mbxs">
            {{ externalVersion.description }}
          </p>
          <template v-if="isLogged">
            <!-- if user is logged in and found -->
            <template v-if="currentUser !== undefined">
              <!-- if project is free -->
              <a v-if="!isPremiumProject" :href="externalVersion.external_url" target="_blank" class="c-version__external-url-button">
                <button type="button" class="c-button c-button--small">
                  <span class="c-button__label u-mrs">Access</span>
                  <i class="c-version__external-url-icon fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                </button>
              </a>
              <!-- if project is premium and user is free -->
              <a v-else-if="isPremiumProject && (!currentUser.forum_user.is_premium && !currentUser.forum_user.vip && !currentUser.forum_user.is_internal_user)" :href="'/subscribe/?subscription=upgrade'" class="c-version__assets-download-button c-version__assets-login-button">
                <button type="button" class="c-button c-button--small">
                  <span class="c-button__asset-label">{{ t('subscribeToDownload') }}</span>
                  <i class="c-version__assets-download-icon fa fa-solid fa-cart-shopping u-mls" aria-hidden="true" />
                </button>
              </a>
              <!-- if project is premium and user is premium -->
              <a v-else-if="isPremiumProject && (currentUser.forum_user.is_premium || currentUser.forum_user.vip || currentUser.forum_user.is_internal_user)" :href="externalVersion.external_url" target="_blank" class="c-version__external-url-button">
                <button type="button" class="c-button c-button--small">
                  <span class="c-button__label u-mrs">Access</span>
                  <i class="c-version__external-url-icon fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                </button>
              </a>
            </template>
            <!-- if user is not found -->
            <template v-else>
              <a :href="'/subscribe/?subscription=login&redirect=' + projectUrl" class="c-version__assets-download-button c-version__assets-login-button">
                <button type="button" class="c-button c-button--small">
                  <span class="c-button__asset-label">{{ t('loginToDownload') }}</span>
                  <i class="c-version__external-url-icon fa-solid fa-arrow-up-right-from-square u-mls" aria-hidden="true" />
                </button>
              </a>
            </template>
          </template>
          <!-- if user is not logged in -->
          <template v-else>
            <a :href="'/subscribe/?subscription=login&redirect=' + projectUrl" class="c-version__assets-download-button c-version__assets-login-button">
              <button type="button" class="c-button c-button--small">
                <span class="c-button__asset-label">{{ t('loginToDownload') }}</span>
                <i class="c-version__external-url-icon fa-solid fa-arrow-up-right-from-square u-mls" aria-hidden="true" />
              </button>
            </a>
          </template>
        </div>
        <div v-if="externalVersion.external_git_repository" class="c-version__external-git-repository u-mbxs">
          <h3 class="c-version__external-git-repository-title">{{ t('externalGitRepository') }}</h3>
          <a :href="externalVersion.external_git_repository" target="_blank" class="c-version__external-git-repository-link">
            {{ cleanUrl(externalVersion.external_git_repository) }}
          </a>
        </div>
        <div
          v-if="deleteExternalVersion.err"
          class="c-message c-message--error u-align-center u-mbs"
        >
          <small>{{ deleteExternalVersion.err }}</small>
        </div>
      </div>
    </div>
  </template>
  <template v-if="isEditExternalVersion">
    <ProjectExternalVersionForm
      v-if="editExternalVersionForm"
      v-model="editExternalVersionForm"
      :project="project"
      :version-id="externalVersion.pk"
      :loading="editExternalVersion.loading"
      :error="editExternalVersion.err"
      :is-edit="true"
      :is-edit-external-version="isEditExternalVersion"
      @canceled="toggleExternalVersionForm"
      @deleted="deleteExternalVersion.exec"
      @submit="editExternalVersion.exec"
    />
  </template>
</template>

<script setup lang="ts">
import { ref, watch, watchEffect } from 'vue'
import {
  useRequest,
  User,
  getUser,
  ProjectExternal,
  editProjectExternalUrl,
  SelectedTopic,
  selectedTopicToTopicField,
  ExternalVersion,
  EditExternalVersion,
  getProjectExternalVersion,
  editProjectExternalVersion,
  deleteProjectVersion
} from '@/utils/api'
import { useI18n } from '@/utils/i18n'
import ProjectExternalVersionForm, { ExternalVersionForm } from '@/components/ProjectExternalVersionForm.vue'
import { useAuth } from '@/utils/auth'

const props = withDefaults(defineProps<{
  project: ProjectExternal
  versionId: number
  isEditExternalVersion?: boolean
}>(), { isEditExternalVersion: false })

const emit = defineEmits<{
  (e: 'canceled'): void
  (e: 'updated'): void
  (e: 'deleted', version: ExternalVersion): void
}>()

const { isLogged, user } = useAuth()
const currentUser = ref<User>()

const project = ref(props.project)
// console.log('PROJECT: ', project.value)
const projectUrl = window.location.pathname

const isPremiumProject = ref(project.value.is_premium)

watch([ isLogged, user ], async () => {
  console.log('isLogged: ', isLogged.value)
  console.log('user: ', user.value)

  if (user.value) {
    await getUserData()
  }
}, { immediate: true })

async function getUserData (): Promise<void> {
  if (user.value) {
    // get forum user
    await getUser(user.value?.pk!)
      .then((result) => {
        currentUser.value = result
      })
      .catch((err) => {
        console.log(err)
      })
  }
}

// using a SelectedTopic ref to handle tags input
const topics = ref<SelectedTopic[]>([])
watchEffect(() => {
  topics.value = project.value.topics.map(t => ({
    id: t.id.toString(),
    text: t.name,
    create: false
  }))
})

const externalVersion = ref(await getProjectExternalVersion(props.versionId))
// console.log('EXTERNAL VERSION: ', externalVersion.value)

const dateFormated = ref('')
if (externalVersion.value.date_published) {
  dateFormated.value = externalVersion.value.date_published.slice(0, 10).replaceAll('-', '/')
} else {
  dateFormated.value = ''
}

if (externalVersion.value.version_number === null) {
  externalVersion.value.version_number = ''
}

// // edit version
// async function selectEditVersion (pk: number) {
//   externalVersion.value = await getProjectVersion(pk)
//   // console.log(externalVersion.value)

//   editExternalVersionForm.value = {
//     name: externalVersion.value.name,
//     description: externalVersion.value.description,
//     versionNumber: externalVersion.value.version_number!,
//     datePublished: externalVersion.value.date_published!,
//     externalUrl: externalVersion.value.external_url
//   }
// }

// init edit external version form values
const editExternalVersionForm = ref<ExternalVersionForm>({
  name: externalVersion.value.name!,
  description: externalVersion.value.description!,
  versionNumber: externalVersion.value.version_number!,
  datePublished: externalVersion.value.date_published!,
  status: externalVersion.value.status!,
  externalUrl: externalVersion.value.external_url,
  externalGitRepository: externalVersion.value.external_git_repository!
})

const editExternalVersion = useRequest(async () => {
  // get version reference to check for changes
  const externalVersionRef = ref(await getProjectExternalVersion(props.versionId))

  const f = editExternalVersionForm.value!

  if (!f.datePublished) {
    f.datePublished = new Date().toISOString().split('T')[0]
  } else {
    f.datePublished = f.datePublished.split('T')[0]
  }

  const body = {
    name: f.name,
    description: f.description,
    version_number: f.versionNumber,
    date_published: f.datePublished,
    status: f.status !== externalVersionRef.value.status ? f.status : externalVersionRef.value.status, // prevent trigger of release:published signal in case of input changes
    external_url: f.externalUrl,
    external_git_repository: f.externalGitRepository
  }
  await editProjectExternalVersion(externalVersion.value.pk, body)
  // update project external url
  // const config = project.value.configuration

  const externalUrlBody = {
    title: project.value.title,
    description: project.value.description,
    topics: topics.value.map(t => selectedTopicToTopicField(t)),
    status: project.value.status,
    // configuration: config,
    date_published: f.datePublished,
    external_url: f.externalUrl,
    external_git_repository: f.externalGitRepository
  }
  await editProjectExternalUrl(props.project.pk, externalUrlBody)

  // update displayed version
  for (const key in body) {
    const k = key as keyof EditExternalVersion
    externalVersion.value[k] = body[k]!
  }

  // emit update to toggle edit form
  emit('updated')
})

const deleteExternalVersion = useRequest(async () => {
  deleteProjectVersion(externalVersion.value.pk)
}, {
  success: () => { emit('deleted', externalVersion.value) }
})

// toggle forms
function toggleExternalVersionForm () {
  emit('canceled')
}

function cleanUrl (url: string) {
  let str = url.replace(/http(s)?(:)?(\/\/)?|(\/\/)?(www\.)?/g, '')
  str = str.replace(/\/+$/, '')
  return str
}

const { t } = useI18n({
  messages: {
    en: {
      edit: 'Edit',
      delete: 'Delete',
      releaseNotes: 'Version notes',
      clickAgainToConfirm: 'Click again to confirm',
      editAVersion: 'Edit a version',
      externalGitRepository: 'External git repository',
      subscribeToDownload: 'Subscribe to download',
      loginToDownload: 'Login to download'
    },
    fr: {
      edit: 'Modifier',
      delete: 'Supprimer',
      releaseNotes: 'Notes de version',
      clickAgainToConfirm: 'Re-cliquer pour confirmer',
      editAVersion: 'Modifier la version',
      externalGitRepository: 'Dépôt git externe',
      subscribeToDownload: 'S\'abonner pour télécharger',
      loginToDownload: 'Se connecter pour télécharger'
    }
  }
})
</script>
